import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Progress } from "./ui/progress";
import {
  KeyRound,
  Copy,
  BarChart3,
  AlertCircle,
  RefreshCw,
  ArrowRight,
  Shield,
  Code2,
  Youtube,
  Globe,
  Activity,
  CheckCircle2,
  Sparkles,
  Zap,
} from "lucide-react";

const Dashboard = ({ apiKey, quotaInfo, loading, onRefresh, error }) => {
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(null);

  const handleCopyKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const handleCopyCommand = (command, index) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(index);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  if (!apiKey) {
    return (
      <div className="min-h-screen bg-gradient-subtle">
        <Helmet>
          <title>Dashboard | RecapMate</title>
        </Helmet>
        
        <section className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto">
            <Card className="shadow-xl text-center">
              <CardContent className="py-16">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-muted flex items-center justify-center">
                  <KeyRound className="h-10 w-10 text-muted-foreground" />
                </div>
                <CardTitle className="text-2xl mb-4">Access Required</CardTitle>
                <CardDescription className="mb-8 text-base">
                  You need an API key to view your dashboard and access RecapMate's features.
                </CardDescription>
                <Button asChild size="lg" className="gradient-primary text-white border-0">
                  <Link to="/buy-credits">
                    Get API Key
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </div>
    );
  }

  const usagePercentage = quotaInfo ? (quotaInfo.remaining / quotaInfo.total) * 100 : 0;
  const isLowCredits = quotaInfo && quotaInfo.remaining < 20;

  const apiExamples = [
    {
      title: "Summarize YouTube Video",
      icon: Youtube,
      iconColor: "text-red-500",
      command: `curl -X GET "${window.location.protocol}//${window.location.hostname}/api/summarize/VIDEO_ID" -H "X-API-Key: ${apiKey}"`,
      description: "Replace VIDEO_ID with the YouTube video identifier",
    },
    {
      title: "Summarize Website",
      icon: Globe,
      iconColor: "text-blue-500",
      command: `curl -X POST "${window.location.protocol}//${window.location.hostname}/api/summarize-website" -H "Content-Type: application/json" -H "X-API-Key: ${apiKey}" -d '{"url":"https://example.com","summaryType":"website-standard"}'`,
      description: "Replace the URL with any website you want to summarize",
    },
    {
      title: "Check Quota",
      icon: Activity,
      iconColor: "text-green-500",
      command: `curl -X GET "${window.location.protocol}//${window.location.hostname}/api/quota" -H "X-API-Key: ${apiKey}"`,
      description: "Returns your current quota information",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <Helmet>
        <title>Dashboard | RecapMate</title>
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center px-4 py-2 mb-6 rounded-full bg-primary/10 text-primary text-sm font-medium animate-in fade-in slide-in duration-500">
              <BarChart3 className="w-4 h-4 mr-2" />
              Dashboard
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight animate-in fade-in slide-in duration-700">
              Welcome Back!
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto animate-in fade-in slide-in duration-900">
              Manage your API key, monitor your usage, and explore integration options
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="max-w-2xl mx-auto mb-8 animate-in fade-in slide-in">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Stats Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {/* API Key Card */}
            <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-500">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <KeyRound className="h-4 w-4 text-primary" />
                    </div>
                    API Key
                  </CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onRefresh}
                    disabled={loading}
                    className="hover-lift"
                  >
                    <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <div className="bg-muted rounded-lg p-3 pr-12 font-mono text-xs overflow-hidden">
                    <code className="text-muted-foreground">
                      {apiKey.substring(0, 20)}...{apiKey.substring(apiKey.length - 8)}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyKey}
                      className="absolute right-2 top-1/2 -translate-y-1/2 hover-lift"
                    >
                      {copiedKey ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Keep this key secure
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Credits Card */}
            <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-600">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                    <Zap className="h-4 w-4 text-accent" />
                  </div>
                  Credits Remaining
                </CardTitle>
              </CardHeader>
              <CardContent>
                {quotaInfo ? (
                  <>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="text-3xl font-bold">{quotaInfo.remaining}</span>
                      <span className="text-muted-foreground">/ {quotaInfo.total}</span>
                    </div>
                    <Progress 
                      value={Math.max(5, Math.min(100, usagePercentage))} 
                      className="h-2"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {Math.round(usagePercentage)}% remaining
                    </p>
                  </>
                ) : loading ? (
                  <div className="h-20 flex items-center justify-center">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Unable to load quota</p>
                )}
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card className="shadow-lg hover-lift animate-in fade-in slide-in duration-700">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Activity className="h-4 w-4 text-green-500" />
                  </div>
                  Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                  </div>
                  <span className="font-medium">Active</span>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  All systems operational
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Low Credits Alert */}
          {isLowCredits && (
            <Alert className="max-w-2xl mx-auto mb-8 bg-amber-500/10 border-amber-500/20 animate-in fade-in slide-in">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="flex items-center justify-between">
                <span>You're running low on credits!</span>
                <Button asChild size="sm" className="ml-4">
                  <Link to="/buy-credits">
                    Top Up
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* API Reference Section */}
          <Card className="shadow-lg animate-in fade-in slide-in duration-800">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-primary flex items-center justify-center">
                  <Code2 className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">API Reference</CardTitle>
                  <CardDescription>
                    Quick examples to get you started with the RecapMate API
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid lg:grid-cols-3 gap-6">
                {apiExamples.map((example, index) => {
                  const Icon = example.icon;
                  return (
                    <div key={index} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${example.iconColor}`} />
                        <h3 className="font-semibold">{example.title}</h3>
                      </div>
                      
                      <div className="relative group">
                        <div className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto">
                          <code className="text-xs block whitespace-pre">
                            {example.command}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCopyCommand(example.command, index)}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity hover-lift"
                          >
                            {copiedCommand === index ? (
                              <CheckCircle2 className="h-3 w-3 text-green-400" />
                            ) : (
                              <Copy className="h-3 w-3 text-slate-400" />
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          {example.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 pt-8 border-t text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Need more credits to keep summarizing?
                </p>
                <Button asChild size="lg" className="gradient-primary text-white border-0 hover-lift">
                  <Link to="/buy-credits">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Buy More Credits
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Dashboard;